import React from 'react';
import { ArrowLeft, Shield, FileText, Users, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* 顶部导航 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            返回
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h1 className="text-xl font-semibold text-gray-900">用户协议与隐私政策</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid gap-6">
          {/* 用户协议 */}
          <Card className="shadow-lg">
            <CardHeader className="bg-blue-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                大悟万能墙用户服务协议
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. 服务条款的接受</h3>
                <p className="text-gray-700 leading-relaxed">
                  欢迎使用大悟万能墙校园社交平台！本协议是您与大悟万能墙平台之间关于使用本服务的法律协议。
                  通过注册、访问或使用我们的服务，您表示同意受本协议的约束。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. 服务描述</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  大悟万能墙是一个专为校园用户设计的社交平台，提供以下服务：
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>发布和浏览校园动态、失物招领、二手交易等信息</li>
                  <li>与其他用户进行互动交流</li>
                  <li>参与校园活动和话题讨论</li>
                  <li>使用匿名发帖功能保护隐私</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. 用户行为规范</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  为维护良好的社区环境，用户在使用本平台时应遵守以下规范：
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>不得发布违法、有害、威胁、辱骂、骚扰、诽谤、粗俗、淫秽或其他不当内容</li>
                  <li>不得冒充他人或虚假陈述与他人的关系</li>
                  <li>不得发布垃圾信息、广告或其他未经授权的商业内容</li>
                  <li>尊重他人的知识产权和隐私权</li>
                  <li>不得进行任何可能损害平台安全性的行为</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4. 账户安全</h3>
                <p className="text-gray-700 leading-relaxed">
                  您有责任维护账户信息的安全性和保密性。您应当立即通知我们任何未经授权使用您账户的情况。
                  我们不对因您未能保护账户信息而造成的任何损失承担责任。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5. 内容所有权</h3>
                <p className="text-gray-700 leading-relaxed">
                  您保留对自己发布内容的所有权，但通过使用我们的服务，您授予我们在平台上使用、展示、
                  分发您内容的非独占性许可。我们有权根据社区规范对违规内容进行处理。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">6. 服务变更与终止</h3>
                <p className="text-gray-700 leading-relaxed">
                  我们保留随时修改、暂停或终止服务的权利。对于违反本协议的用户，我们有权暂停或终止其账户。
                  协议的重大变更将通过平台通知用户。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">7. 免责声明</h3>
                <p className="text-gray-700 leading-relaxed">
                  本服务按"现状"提供，我们不对服务的可用性、准确性或完整性做出任何保证。
                  在法律允许的最大范围内，我们不承担任何间接、偶然或后果性损害的责任。
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>生效日期：</strong>2024年1月1日<br />
                  <strong>最后更新：</strong>2024年8月13日
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 隐私政策 */}
          <Card className="shadow-lg">
            <CardHeader className="bg-green-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                隐私保护政策
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. 信息收集</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  我们收集以下类型的信息以提供更好的服务：
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>账户信息：</strong>用户名、邮箱、学校信息、年级专业等</li>
                  <li><strong>内容信息：</strong>您发布的帖子、评论、图片等内容</li>
                  <li><strong>使用信息：</strong>访问日志、设备信息、IP地址等技术信息</li>
                  <li><strong>互动信息：</strong>点赞、评论、关注等社交互动数据</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. 信息使用</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  我们使用收集的信息用于：
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>提供、维护和改进我们的服务</li>
                  <li>个性化用户体验和内容推荐</li>
                  <li>处理用户请求和提供客户支持</li>
                  <li>维护平台安全和防止滥用</li>
                  <li>遵守法律法规要求</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. 信息共享</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  我们不会出售您的个人信息。在以下情况下，我们可能会共享您的信息：
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>获得您的明确同意</li>
                  <li>遵守法律法规、法院命令或政府要求</li>
                  <li>保护我们的权利、财产或安全</li>
                  <li>与可信的第三方服务提供商合作（仅限必要信息）</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4. 数据安全</h3>
                <p className="text-gray-700 leading-relaxed">
                  我们采用行业标准的安全措施保护您的信息，包括数据加密、访问控制、安全审计等。
                  但请注意，没有任何系统是100%安全的，我们无法保证绝对的安全性。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5. 用户权利</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  您对自己的个人信息享有以下权利：
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>访问和查看您的个人信息</li>
                  <li>更正不准确的个人信息</li>
                  <li>删除您的个人信息（在法律允许的范围内）</li>
                  <li>限制或反对某些信息处理</li>
                  <li>数据可携带权</li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Cookie和追踪技术</h3>
                <p className="text-gray-700 leading-relaxed">
                  我们使用Cookie和类似技术来改善用户体验、分析使用情况和提供个性化服务。
                  您可以通过浏览器设置管理Cookie偏好。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">7. 未成年人保护</h3>
                <p className="text-gray-700 leading-relaxed">
                  我们特别重视未成年人的隐私保护。如果您未满18岁，请在家长或监护人的指导下使用我们的服务。
                  我们不会故意收集13岁以下儿童的个人信息。
                </p>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">8. 联系我们</h3>
                <p className="text-gray-700 leading-relaxed">
                  如果您对本隐私政策有任何疑问或需要行使您的权利，请通过以下方式联系我们：
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-3">
                  <p className="text-sm text-gray-700">
                    <strong>邮箱：</strong>privacy@dawu-wall.com<br />
                    <strong>地址：</strong>湖北省孝感市大悟县<br />
                    <strong>客服热线：</strong>400-123-4567
                  </p>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>生效日期：</strong>2024年1月1日<br />
                  <strong>最后更新：</strong>2024年8月13日
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 底部操作区 */}
          <div className="flex justify-center gap-4 pt-6">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="px-8"
            >
              返回
            </Button>
            <Button
              onClick={() => {
                // 这里可以添加同意逻辑
                navigate('/register');
              }}
              className="px-8 bg-blue-600 hover:bg-blue-700"
            >
              我已阅读并同意
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;